import React, { FC, useState } from 'react';
import styles from './styles.module.css';
import { Table, Asset as SharedAsset } from 'Shared/components';
import { currency, formatMoney } from 'Shared/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { PaymentScheduleChangesType } from '@clearcover/core-app-types';

type PaymentScheduleProps = {
  paymentScheduleChanges: PaymentScheduleChangesType;
};

const headers = [
  'DUE DATE',
  'PREVIOUS TOTAL',
  'NEW TOTAL',
  'CHANGE $/%',
  'DESCRIPTION',
];

const PaymentBreakdown: FC<{
  paymentScheduleChanges: PaymentScheduleChangesType[0];
}> = ({ paymentScheduleChanges }) => {
  const premiumCharges = buildTotals(
    paymentScheduleChanges.previousPremiumCharges,
    paymentScheduleChanges.newPremiumCharges
  );
  const feeCharges = buildTotals(
    paymentScheduleChanges.previousFeeCharges,
    paymentScheduleChanges.newFeeCharges
  );
  const mscCharges = buildTotals(
    paymentScheduleChanges.previousMscCharges,
    paymentScheduleChanges.newMscCharges
  );
  const taxCharges = buildTotals(
    paymentScheduleChanges.previousTaxCharges,
    paymentScheduleChanges.newTaxCharges
  );
  return (
    <div className={styles.paymentBreakdown}>
      <div className={styles.paymentBreakdownItem}>
        <div>Premium Amount</div>
        <div>{premiumCharges.previousTotal}</div>
        <div>{premiumCharges.newTotal}</div>
        <div>{premiumCharges.change}</div>
      </div>
      <div className={styles.paymentBreakdownItem}>
        <div>Fees Due</div>
        <div>{feeCharges.previousTotal}</div>
        <div>{feeCharges.newTotal}</div>
        <div>{feeCharges.change}</div>
      </div>
      <div className={styles.paymentBreakdownItem}>
        <div>Surplus Due</div>
        <div>{mscCharges.previousTotal}</div>
        <div>{mscCharges.newTotal}</div>
        <div>{mscCharges.change}</div>
      </div>
      <div className={styles.paymentBreakdownItem}>
        <div>Taxes</div>
        <div>{taxCharges.previousTotal}</div>
        <div>{taxCharges.newTotal}</div>
        <div>{taxCharges.change}</div>
      </div>
    </div>
  );
};

export const PaymentSchedule: FC<PaymentScheduleProps> = ({
  paymentScheduleChanges,
}) => {
  const [openPayments, setOpenPayments] = useState<number[]>([]);

  const newOpenPayments = (current: number[], index: number) =>
    current.includes(index)
      ? current.filter((i) => i !== index)
      : [...current, index];

  return (
    <>
      <Table
        headers={headers}
        columnWidths={[1, 1, 1, 1, 2]}
        className={styles.table}
        striped={false}
        rows={paymentScheduleChanges.map((installment, index) => {
          const { previousTotal, newTotal, change } = buildTotals(
            installment.previousTotalCharges,
            installment.newTotalCharges
          );

          const handleClick = () =>
            setOpenPayments((current) => newOpenPayments(current, index));

          return {
            key: index,
            data: [
              installment.dueDate,
              previousTotal,
              newTotal,
              change,
              <div className={styles.description}>
                <span>{installment.description}</span>
                <div
                  className={styles.transactionDetailsToggle}
                  onClick={handleClick}
                  onKeyDown={handleClick}
                >
                  <motion.div
                    initial={false}
                    exit="exit"
                    animate={{
                      transition: { ease: 'easeIn' },
                      rotate: openPayments.includes(index) ? 180 : 0,
                    }}
                  >
                    <SharedAsset variant="upArrow" />
                  </motion.div>
                </div>
              </div>,
              <AnimatePresence initial={false}>
                {openPayments.includes(index) && (
                  <motion.div
                    key="content"
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={{
                      open: {
                        opacity: 1,
                        height: 'auto',
                        transition: { ease: 'easeIn' },
                      },
                      collapsed: {
                        opacity: 0,
                        height: 0,
                        transition: { ease: 'easeOut' },
                      },
                    }}
                  >
                    <PaymentBreakdown paymentScheduleChanges={installment} />
                  </motion.div>
                )}
              </AnimatePresence>,
            ],
          };
        })}
      />
    </>
  );
};

function buildTotals(rawPreviousTotal: string, rawNewTotal: string) {
  const previousTotal = currency(rawPreviousTotal);
  const newTotal = currency(rawNewTotal);
  const rawChange = newTotal.subtract(previousTotal);

  const symbol = rawChange.value < 0 ? '-' : '+';

  const amountChange = formatMoney(Math.abs(rawChange.value));

  const rawPercentChange = rawChange.divide(previousTotal).multiply(100);
  const percentChange = Math.abs(rawPercentChange.value) || 0;

  return {
    previousTotal: previousTotal.format(),
    newTotal: newTotal.format(),
    change:
      previousTotal.value === 0 && newTotal.value > 0
        ? `${symbol}${amountChange}`
        : `${symbol}${amountChange}/${percentChange}%`,
  };
}
